'use client';

import { LinearProgress as MuiLinearProgress } from '@mui/material';
import clsx from 'clsx';

import { getMeterColorByPercentage } from '@web/constants';

import styles from './Meter.module.scss';
import { Tooltip } from './Tooltip';

type MeterProps = {
  percentage: number;
  label?: React.ReactNode;
  tooltipContent?: React.ReactNode;
  ariaLabel?: string;
  className?: string;
  actualValue?: number;
  allocatedValue?: number;
  estimatedValue?: number;
};

function clampPercentage(value: number) {
  return Math.max(0, Math.min(100, value));
}

function getAllocationValues({
  actualValue,
  allocatedValue,
  estimatedValue,
}: Pick<MeterProps, 'actualValue' | 'allocatedValue' | 'estimatedValue'>) {
  if (
    typeof actualValue !== 'number' ||
    typeof allocatedValue !== 'number' ||
    typeof estimatedValue !== 'number'
  ) {
    return null;
  }

  const actual = Math.max(0, actualValue);
  const allocated = Math.max(0, allocatedValue);
  const estimated = Math.max(0, estimatedValue);
  const scale = Math.max(estimated, allocated, actual, 1);

  return {
    fillPercent: clampPercentage((actual / scale) * 100),
    tickPercent: clampPercentage((allocated / scale) * 100),
    estimatePercent: clampPercentage((estimated / scale) * 100),
    overAllocatedPercent:
      allocated > estimated
        ? clampPercentage(((allocated - estimated) / scale) * 100)
        : 0,
    isOverAllocated: allocated > estimated,
  };
}

export function Meter({
  percentage,
  label,
  tooltipContent,
  ariaLabel,
  className,
  actualValue,
  allocatedValue,
  estimatedValue,
}: MeterProps) {
  const progressValue = clampPercentage(percentage);
  const meterColor = getMeterColorByPercentage(percentage);
  const allocationValues = getAllocationValues({
    actualValue,
    allocatedValue,
    estimatedValue,
  });

  const trackNode = allocationValues ? (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(allocationValues.fillPercent)}
      className={styles.meter__allocationTrack}
    >
      <span
        className={styles.meter__allocationEstimate}
        style={{ width: `${allocationValues.estimatePercent}%` }}
      />
      <span
        className={styles.meter__allocationFill}
        style={{ width: `${allocationValues.fillPercent}%` }}
      />
      {allocationValues.isOverAllocated ? (
        <span
          className={styles.meter__allocationOverage}
          style={{
            left: `${allocationValues.estimatePercent}%`,
            width: `${allocationValues.overAllocatedPercent}%`,
          }}
        />
      ) : null}
      <span
        className={styles.meter__allocationTick}
        style={{ left: `${allocationValues.tickPercent}%` }}
      />
    </div>
  ) : (
    <MuiLinearProgress
      variant="determinate"
      value={progressValue}
      sx={{
        backgroundColor: 'var(--nn-color-meter-background)',
        borderRadius: '999px',
        height: 8,
        '& .MuiLinearProgress-bar': {
          backgroundColor: `${meterColor} !important`,
          borderRadius: '999px',
        },
      }}
    />
  );

  const meterNode = (
    <div
      className={clsx(styles.meter, className)}
      aria-label={ariaLabel}
      data-meter-color={meterColor}
    >
      {trackNode}

      {label ? <span className={styles.meter__label}>{label}</span> : null}
    </div>
  );

  if (!tooltipContent) {
    return meterNode;
  }

  return (
    <Tooltip
      title={tooltipContent}
      wrapperStyle={{ display: 'flex', width: '100%' }}
    >
      <span className={styles.meter__tooltipTarget}>{meterNode}</span>
    </Tooltip>
  );
}
